import "./PaymentVoucherSetupRightTablePanelStyle.css";

import { CircularProgress, Grid, useTheme } from "@mui/material";
import { EnhancedButtonPrimeReact, EnhancedButtonPrimeReactSeverity, EnhancedRadioGroupRHF } from "@websential/cosmic-v20";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BasicAddRowButtonToolbar } from "../../../common/BasicAddRowButtonToolbar";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { TransactionsServices } from "../../../services/DirectDeposit/TransactionsServices";
import { formatAmountCommaSeperator } from "../../../common/Common";

// ─── Static DDL Data ──────────────────────────────────────────────────────────

const PAYMENT_VOUCHER_TYPE_DDL = [
  { label: "--", value: null },
  { label: "Payroll", value: 400 },
  { label: "CRA", value: 100 },
  { label: "RQ", value: 500 },
  { label: "Invoice", value: 300 },
  { label: "EHT", value: 200 },
];

const PERIOD_TYPE_DDL_DEFAULT = [
  { label: "--", value: null },
  { label: "Monthly", value: 200 },
  { label: "Semi Monthly", value: 300 },
  { label: "BiWeekly", value: 100 },
  { label: "Weekly", value: 400 },
];

const EMPTY_ROW = {
  id: null as string | null,
  type: null as number | null,
  periodType: null as number | null,
  periodDate: null as any,
  amount: null as number | null,
  comments: null as string | null,
};

const MAX_ROWS = 10;

const createRowId = (): string =>
  Math.random().toString(36).slice(2, 7).toUpperCase();

interface IPaymentVoucherSetupRightTablePanel {
  paymentVoucherFieldsTableUpdatedData: (data: any) => void;
  paymentVoucherFieldsDataItems: any;
  sumOfAllAmounts: (value: any) => void;
  PaymentVoucherPeriodTypeDDL: { label: string; value: any }[];
  /** Controls whether period-date column shows Calendar (200) or Dropdown (else) */
  transactionType: number | null;
  /**
   * Increment this whenever the parent wants to fully reset the table
   * (accountant change, client change, transaction-type change).
   * Using a key-like prop avoids complex internal logic.
   */
  resetKey: number;
}
export const PaymentVoucherSetupRightTablePanel: FC<
  IPaymentVoucherSetupRightTablePanel
> = ({ paymentVoucherFieldsTableUpdatedData, paymentVoucherFieldsDataItems, sumOfAllAmounts,

  transactionType,
  PaymentVoucherPeriodTypeDDL,
  resetKey,
}) => {

    // Table rows
    const [rows, setRows] = useState<any[]>(paymentVoucherFieldsDataItems);
    const [dataReadyKey, setDataReadyKey] = useState(0);
    const syncRequestIdRef = useRef(0);

    // ID of the most-recently user-added row (shows delete button)
    const [lastAddedRowId, setLastAddedRowId] = useState<string | null>(null);

    /**
     * Per-row period-date DDL options.
     * Key = rowIndex (number), Value = { label, value }[]
     * Only populated for transactionType !== 200.
     */
    const [periodDateDDLMap, setPeriodDateDDLMap] = useState<
      Map<number, { label: string; value: any }[]>
    >(new Map());

    // ─── Column Definitions ─────────────────────────────────────────────────────

    const isCustom = transactionType === 200;

    // ── Sync rows when parent data changes (load / reset from outside) ──────────
    useEffect(() => {
      setRows(paymentVoucherFieldsDataItems);
      setLastAddedRowId(null);

      if (isCustom) {
        setPeriodDateDDLMap(new Map());
        setDataReadyKey((k) => k + 1); // nothing async to wait for — remount now
        return;
      }

      const rowsNeedingOptions = paymentVoucherFieldsDataItems
        .map((row: any, index: number) => ({ index, periodType: row.periodType }))
        .filter((r: any) => r.periodType !== null && r.periodType !== undefined);

      if (rowsNeedingOptions.length === 0) {
        setPeriodDateDDLMap(new Map());
        setDataReadyKey((k) => k + 1);
        return;
      }

      const requestId = ++syncRequestIdRef.current;

      Promise.all(
        rowsNeedingOptions.map(({ index, periodType }: any) =>
          TransactionsServices.getPeriodDatesByScheduleId(periodType)
            .then((response: any) => ({ index, response }))
            .catch(() => ({ index, response: [] }))
        )
      ).then((results) => {
        if (requestId !== syncRequestIdRef.current) return; // superseded by a newer sync

        const next = new Map();
        results.forEach(({ index, response }) => next.set(index, response));
        setPeriodDateDDLMap(next);

        // All API calls are done and the map is populated — NOW force the table to remount
        setDataReadyKey((k) => k + 1);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentVoucherFieldsDataItems, resetKey, isCustom]);

    // ── Full reset when resetKey increments (accountant / client / tx-type change)
    useEffect(() => {
      setRows(paymentVoucherFieldsDataItems);
      setLastAddedRowId(null);
      setPeriodDateDDLMap(new Map());
      // intentionally exclude paymentVoucherFieldsDataItems from deps here;
      // we only want this to fire on explicit resets driven by resetKey.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);

    // ── Notify parent whenever rows change ────────────────────────────────────
    useEffect(() => {
      paymentVoucherFieldsTableUpdatedData(rows);
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Update running total ──────────────────────────────────────────────────
    useEffect(() => {
      const total = rows.reduce(
        (acc: number, row: any) => acc + (Number(row.amount) || 0),
        0
      );
      sumOfAllAmounts(total);
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Handlers ───────────────────────────────────────────────────────────────

    const addRow = useCallback(() => {
      setRows((prev) => {
        if (prev.length >= MAX_ROWS) return prev;
        const newRow = { ...EMPTY_ROW, id: createRowId() };
        setLastAddedRowId(newRow.id);
        return [...prev, newRow];
      });
    }, []);

    const deleteRow = useCallback(
      (rowId: string) => {
        setRows((prev) => {
          const next = prev.filter((r) => r.id !== rowId);
          if (rowId === lastAddedRowId) {
            // Promote the previous user-added row as the new "last added"
            const prevAdded = [...next]
              .reverse()
              .find(
                (r) =>
                  !paymentVoucherFieldsDataItems.some(
                    (initial: any) => initial.id === r.id
                  )
              );
            setLastAddedRowId(prevAdded?.id ?? null);
          }
          return next;
        });
      },
      [lastAddedRowId, paymentVoucherFieldsDataItems]
    );

    const updateCell = useCallback(
      async (rowIndex: number, field: string, value: any) => {
        // When period-type changes on a non-custom voucher, fetch period-date options for that row
        if (field === "periodType" && transactionType !== 200 && value !== null) {
          try {
            // Replace with real API call: TransactionsServices.getPeriodsDDLByPeriodType(value)
            const response = await TransactionsServices.getPeriodDatesByScheduleId(value)
            // const response: { label: string; value: any }[] = [
            //   { label: "--", value: null },
            //   { label: "Jan 12 2025", value: "123456789" },
            //   { label: "Apr 23 2026", value: "789456" },
            // ];
            setPeriodDateDDLMap((prev) => {
              const next = new Map(prev);
              next.set(rowIndex, response);
              return next;
            });
          } catch {
            // silently ignore; period date DDL stays empty
          }
        }

        // When period-type is cleared, also clear that row's period-date DDL
        if (field === "periodType" && value === null) {
          setPeriodDateDDLMap((prev) => {
            const next = new Map(prev);
            next.delete(rowIndex);
            return next;
          });
        }

        setRows((prev) => {
          const next = [...prev];
          next[rowIndex] = { ...next[rowIndex], [field]: value };
          // If period-type changed, reset period-date for that row
          if (field === "periodType") {
            next[rowIndex] = { ...next[rowIndex], periodDate: null };
          }
          return next;
        });
      },
      [transactionType]
    );

    const columns: {
      field: string;
      header: string;
      footer?: string;
    }[] = [
        { field: "type", header: "Type" },
        {
          field: "periodType",
          header: "Period Type",
          footer: "Total Amounts",
        },
        { field: "periodDate", header: "Period Date" },
        {
          field: "amount",
          header: "Amount",
          footer: formatAmountCommaSeperator(
            rows.reduce((acc: number, r: any) => acc + (Number(r.amount) || 0), 0)
          ),
        },
        { field: "comments", header: "Comments" },
      ];

    const isLoadingPeriodType = useMemo(
      () => PaymentVoucherPeriodTypeDDL.some(item => item.label === "Load client first"),
      [PaymentVoucherPeriodTypeDDL]
    );

    // ─── Cell Renderers ─────────────────────────────────────────────────────────

    const renderCell = (rowData: any, rowMeta: any, col: { field: string }) => {
      const { rowIndex } = rowMeta;

      switch (col.field) {
        case "type":
          return (
            <Dropdown
              options={PAYMENT_VOUCHER_TYPE_DDL}
              value={rowData.type}
              onChange={(e) => updateCell(rowIndex, "type", e.value)}
              placeholder="--"
              optionLabel="label"
              optionValue="value"
              style={{ borderRadius: "2px", minWidth: "150px" }}
            />
          );

        case "periodType":
          return (
            <Dropdown
              options={
                isCustom ? PERIOD_TYPE_DDL_DEFAULT : PaymentVoucherPeriodTypeDDL
              }
              value={isCustom ? Number(rowData.periodType) : rowData.periodType}
              onChange={(e) => updateCell(rowIndex, "periodType", e.value)}
              placeholder="--"
              optionLabel="label"
              optionValue="value"
              style={{ borderRadius: "2px", minWidth: "150px" }}
              disabled={isLoadingPeriodType}
            />
          );

        case "periodDate":
          if (isCustom) {
            // Always show calendar for Custom Voucher (transactionType === 200)
            return (
              <Calendar
                value={rowData.periodDate}
                showIcon
                style={{ height: "2.6rem", minWidth: "180px" }}
                onChange={(e) => updateCell(rowIndex, "periodDate", e.value)}
                dateFormat="M dd, yy"
                showButtonBar
                className="calender"
              />
            );
          }
          // For Company / Employee vouchers: show dropdown populated by period-type selection
          return (
            <Dropdown
              options={periodDateDDLMap.get(rowIndex) ?? []}
              value={rowData.periodDate}
              onChange={(e) => updateCell(rowIndex, "periodDate", e.value)}
              placeholder={
                rowData.periodType === null
                  ? "Select period type first"
                  : "--"
              }
              optionLabel="label"
              optionValue="value"
              disabled={!periodDateDDLMap.has(rowIndex)}
              style={{ borderRadius: "2px", minWidth: "180px" }}
            />
          );

        case "amount":
          return (
            <InputNumber
              value={rowData.amount}
              onChange={(e) => updateCell(rowIndex, "amount", e.value)}
              placeholder="0.00"
              minFractionDigits={2}
              maxFractionDigits={2}
              inputStyle={{
                textAlign: "right",
                borderRadius: "2px",
                minWidth: "100px",
              }}
            />
          );

        case "comments":
          return (
            <InputText
              value={rowData.comments ?? ""}
              onChange={(e) => updateCell(rowIndex, "comments", e.target.value)}
              className="w-full"
              style={{ textAlign: "left", borderRadius: "2px", minWidth: "280px" }}
            />
          );

        default:
          return null;
      }
    };

    return (
      <>
        <Grid className="card" sx={{
          background: "#fff",
          // border: "1px solid #e5e7eb",
          // borderBottom: 0,
          borderRadius: "3px",
          padding: "31px 20px 1rem 20px",
          marginBottom: "0px!important",
          "& .MuiToolbar-root": { width: "100%" },
          border: "1px solid rgba(0, 0, 0, 0.12)"
        }}>
          <BasicAddRowButtonToolbar openNew={addRow} />
          <DataTable
            value={rows}
            dataKey="id"
            /**
             * resetKey in the DataTable key ensures the component fully re-mounts
             * whenever the parent signals a reset, clearing all internal PrimeReact state.
             */
            key={`cv-table-${resetKey}-${dataReadyKey}`}
            showGridlines
            className="PaymentVoucherSetupRightTablePanelStyleCss"
            editMode="cell"
            resizableColumns
            columnResizeMode="expand"
            scrollable
            filterDisplay="menu"
            scrollHeight="516px"
          >
            {/* {columns.map((x: any, index: any) => {
            return (
              <Column
                key={index}
                field={x.field}
                header={x.header}
                columnKey={x.field}
                footer={x.footer}
                footerStyle={{ textAlign: x.field === 'amount' ? 'right' : 'left', padding: "6px", borderBottom: "0" }}
                headerStyle={{ borderTop: "0" }}
                style={{
                  fontSize: "0.875rem",
                }}
                body={(rowData: any, row: any) => columnsBody(rowData, row, x)}
              />
            );
          })}

          {lastAddedRowId && <Column
            body={(rowData: any, row: any) => {
              const showIcon = rowData.id === lastAddedRowId;
              

              return showIcon ? (
                <EnhancedButtonPrimeReact
                  icon="pi pi-times"
                  severity={EnhancedButtonPrimeReactSeverity.Danger}
                  className="p-button-rounded p-button-text shadow-none bg-transparent"
                  rounded
                  // onClickHandler={onDeleteNewAddedRow}
                  onClickHandler={() => onDeleteNewAddedRow(rowData.id)}
                />
              ) : null;
            }}
          />} */}

            {columns.map((col, idx) => (
              <Column
                key={idx}
                field={col.field}
                header={col.header}
                columnKey={col.field}
                footer={col.footer}
                footerStyle={{
                  textAlign: col.field === "amount" ? "right" : "left",
                  padding: "6px",
                  borderBottom: "0",
                }}
                headerStyle={{ borderTop: "0" }}
                style={{ fontSize: "0.875rem" }}
                body={(rowData, rowMeta) => renderCell(rowData, rowMeta, col)}
              />
            ))}

            {/* Delete button column — only visible on the last user-added row */}
            {lastAddedRowId && (
              <Column
                body={(rowData) =>
                  rowData.id === lastAddedRowId ? (
                    <EnhancedButtonPrimeReact
                      icon="pi pi-times"
                      severity={EnhancedButtonPrimeReactSeverity.Danger}
                      className="p-button-rounded p-button-text shadow-none bg-transparent"
                      rounded
                      onClickHandler={() => deleteRow(rowData.id)}
                    />
                  ) : null
                }
              />
            )}
          </DataTable>
        </Grid>
      </>
    );
  };
